import React, { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { Dropdown, Pagination, Table as AntdTable, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IAffiliateCommission } from 'v2/interfaces/reports/affiliateCommissions';
import { affiliateCommissionsService } from 'v2/services/reports/affiliateCommissions';

interface IProps {
  handleSeeDetailsClick: (affiliate_id: number) => void;
}

export const Table = ({ handleSeeDetailsClick }: IProps) => {
  useObservable(() => affiliateCommissionsService.loadCommissions(), []);
  const [commissions] = useObservable(() => affiliateCommissionsService.getCommissions(), []);
  const [isLoading] = useObservable(() => affiliateCommissionsService.isLoading(), []);
  const [pagination] = useObservable(() => affiliateCommissionsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      affiliateCommissionsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
      document.getElementById('commissions-affiliates-table')?.scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      affiliateCommissionsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('commissions-affiliates-table')?.scrollIntoView();
    },
    [pagination]
  );

  const columns: ColumnsType<IAffiliateCommission> = useMemo(
    () => [
      {
        title: 'Afiliado',
        key: 'affiliate',
        width: 440,
        render: (_: unknown, commission) => {
          if (!commission?.name) {
            return <span>-</span>;
          }

          return (
            <div className='flex flex-col items-start gap-1'>
              <Typography.Text>
                <Truncate count={25} final='...'>
                  <Typography.Link
                    href={`https://app.selectafiliados.com.br/affiliate/detailsSource/${commission.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {commission.name}
                  </Typography.Link>
                </Truncate>
              </Typography.Text>
              <Typography.Text type='secondary'>
                <Truncate count={25} final='...'>
                  {commission.email || ''}
                </Truncate>
              </Typography.Text>
            </div>
          );
        }
      },
      {
        title: 'Quantidade de Comissões',
        dataIndex: 'total_commissions',
        key: 'total_commissions',
        width: 400,
        render: (value: number) => <Typography.Text>{value ?? 0}</Typography.Text>
      },
      {
        title: 'Total de comissão paga (líquido)',
        dataIndex: 'total_gain',
        key: 'total_gain',
        width: 300,
        align: 'right',
        render: (value: number) => (
          <Typography.Text strong>
            <FormattedNumber value={value ?? 0} format='BRL' />
          </Typography.Text>
        )
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, commission) => {
          const items: MenuProps['items'] = [
            {
              key: 'details',
              label: 'Ver detalhes',
              onClick: () => handleSeeDetailsClick(commission.id)
            }
          ];

          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <TableActionButton onClick={() => undefined} />
            </Dropdown>
          );
        }
      }
    ],
    [handleSeeDetailsClick]
  );

  return (
    <div
      id='commissions-affiliates-table'
      data-testid='commissions-affiliates-table'
      className='w-full overflow-x-auto overflow-y-hidden'
    >
      <AntdTable<IAffiliateCommission>
        className='min-w-225 [&_.ant-table-body]:overflow-y-hidden [&_.ant-table-content]:overflow-y-hidden [&_.ant-table-tbody_.ant-typography]:text-sm'
        rowKey='id'
        columns={columns}
        dataSource={commissions || []}
        loading={!!isLoading}
        pagination={false}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
      {pagination && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            current={pagination.current_page || 1}
            total={pagination.total_items || 0}
            pageSize={pagination.per_page || 10}
            pageSizeOptions={['10', '25', '50']}
            showSizeChanger
            onChange={onChangePage}
            onShowSizeChange={(_, size) => onChangePerPage(size)}
          />
        </div>
      )}
    </div>
  );
};
